import { useMemo } from "react";

import { useFavouritesContext } from "features/favourites/hooks/useFavouritesContext";
import useRecommendedVehicles from "features/reviews/hooks/useRecommendedVehicles";
import type { ResolvedShopfront } from "resolvers/data/shopfrontResolver";
import { ResolvedVehicleCardSettings } from "resolvers/data/vehicleCardSettingsResolver";
import { ResolvedVehicleDisplaySettings } from "resolvers/pages/pdpPageResolver";
import type { ITaxonomyGroup } from "services/content-manager/models";
import findTaxonomy from "utils/findTaxonomy";

import CarouselView from "./CarouselView";
import FromView from "./FromView";

type ShopfrontProps = ResolvedShopfront & {
    taxonomy?: ITaxonomyGroup;
    vehicleCardSettings?: ResolvedVehicleCardSettings;
    vehicleDisplaySettings: ResolvedVehicleDisplaySettings;
};

const VIEWS = {
    carousel: CarouselView,
    from: FromView,
};

const Shopfront = (props: ShopfrontProps) => {
    const {
        view = "from",
        taxonomy,
        vehicleCodename,
        vehicleCardSettings,
        vehicleDisplaySettings,
    } = props;

    const vehicleTaxonomy = useMemo(() => {
        if (!taxonomy?.terms.length || !vehicleCodename) return null;

        return findTaxonomy(taxonomy, vehicleCodename);
    }, [taxonomy, vehicleCodename]);

    const [_, make, model, variant] = vehicleTaxonomy || [];

    const { vehicles, total } = useRecommendedVehicles({
        make: make.name,
        model: model?.name || "",
        variant: variant?.name || "",
    });

    const { useShortlist } = useFavouritesContext();
    const shortlist = useShortlist();

    const renderedView = total > 3 && view !== "from" ? "carousel" : "from";

    if (!vehicleTaxonomy || !total) return null;

    const View = VIEWS[renderedView];

    return (
        <View
            {...props}
            vehicleMakeModel={`${make.name} ${model?.name || ""} ${variant?.name || ""}`}
            vehicles={vehicles}
            shortlistedStocks={shortlist?.stockNumbers ?? []}
            vehicleCardSettings={vehicleCardSettings}
            vehicleDisplaySettings={vehicleDisplaySettings}
        />
    );
};

export default Shopfront;
